<template>
  <div>
    <!-- Trigger -->
    <div class="flex items-center gap-2">
      <span v-if="optimizeError" class="text-[10px] text-red-500">{{ optimizeError }}</span>
      <button
        type="button"
        :disabled="isOptimizing || !hasBullets"
        @click="startOptimization"
        class="flex items-center gap-1 px-2.5 py-0.5 rounded-full border border-gold/60 text-gold-600 dark:border-gold-400/50 dark:text-gold-400 text-[10px] font-semibold hover:bg-gold-50 dark:hover:bg-gold-400/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        :title="hasBullets ? 'Improve these bullet points with AI' : 'Add a bullet point first'"
      >
        <svg v-if="!isOptimizing" class="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/>
        </svg>
        <svg v-else class="w-2.5 h-2.5 flex-shrink-0 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        {{ isOptimizing ? 'Optimizing…' : 'Optimize with AI' }}
      </button>
    </div>

    <!-- Review modal (styled to match CvCreationWizard) -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showReview" class="optimizer-backdrop" @click.self="closeReview">
          <div class="optimizer-panel">

            <!-- Header -->
            <div class="optimizer-header">
              <div>
                <p class="optimizer-step-label">AI Optimizer</p>
                <h2 class="optimizer-title">Review suggestions</h2>
              </div>
              <button @click="closeReview" class="optimizer-close">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Body -->
            <div class="optimizer-body">
              <p class="text-xs text-zinc-400 leading-relaxed mb-4">
                Select the suggestions you want to apply. Your original text is never changed without your approval.
              </p>
              <div class="space-y-3">
                <div
                  v-for="(item, i) in reviewItems"
                  :key="i"
                  class="suggestion-card"
                  :class="{ 'suggestion-card--selected': item.accepted, 'suggestion-card--static': item.unchanged }"
                  @click="item.unchanged || (item.accepted = !item.accepted)"
                >
                  <div v-if="item.accepted" class="suggestion-card-check">
                    <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <template v-if="item.unchanged">
                    <p class="text-xs text-zinc-600 dark:text-zinc-300 pr-6">{{ item.original }}</p>
                    <p class="text-[10px] text-zinc-400 dark:text-zinc-500 mt-1">Already strong — no change suggested</p>
                  </template>
                  <template v-else>
                    <p class="text-xs text-zinc-400 dark:text-zinc-500 line-through decoration-zinc-300 dark:decoration-zinc-600 pr-6">{{ item.original }}</p>
                    <p class="text-xs text-zinc-900 dark:text-white mt-1 pr-6">{{ item.suggestion }}</p>
                    <p
                      v-if="item.suggestion.length > maxBulletChars"
                      class="text-[10px] text-amber-500 dark:text-amber-400 mt-0.5 tabular-nums"
                      title="Longer bullets may wrap to a second line in the PDF"
                    >{{ item.suggestion.length }}/{{ maxBulletChars }} — may wrap in the PDF</p>
                  </template>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="optimizer-footer">
              <button type="button" @click="toggleAll" class="opt-btn-secondary">
                {{ allAccepted ? 'Deselect all' : 'Accept all' }}
              </button>
              <div class="flex items-center gap-2">
                <button type="button" @click="closeReview" class="opt-btn-secondary">
                  Cancel
                </button>
                <button type="button" :disabled="acceptedCount === 0" @click="applyAccepted" class="opt-btn-primary">
                  Apply {{ acceptedCount }} {{ acceptedCount === 1 ? 'suggestion' : 'suggestions' }}
                </button>
              </div>
            </div>

          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import { useBulletOptimizer } from '../composables/useBulletOptimizer.js'
import { CV_CONFIG } from '../config/cvConfig.js'

export default {
  name: 'BulletOptimizer',
  props: {
    entryType: {
      type: String,
      required: true,
      validator: (v) => ['experience', 'education', 'activity'].includes(v)
    },
    bullets: {
      type: Array,
      required: true
    },
    // Entry fields used to ground the AI prompt (jobTitle, company, ...)
    context: {
      type: Object,
      default: () => ({})
    },
    // Saved CV entry id → backend resolves the entry's reference documents
    cvEntryId: {
      type: [Number, String],
      default: null
    },
    // Master entry id → reference-documents fallback for unsaved CV entries
    masterEntryId: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['apply'],
  setup(props, { emit }) {
    const { isOptimizing, optimizeError, optimizeEntry, clearOptimizeError } = useBulletOptimizer()

    const showReview = ref(false)
    const reviewItems = ref([])

    const nonEmptyBullets = computed(() => props.bullets.map(b => (b || '').trim()).filter(b => b !== ''))
    const hasBullets = computed(() => nonEmptyBullets.value.length > 0)

    const startOptimization = async () => {
      clearOptimizeError()

      const result = await optimizeEntry({
        entryType: props.entryType,
        bullets: nonEmptyBullets.value,
        context: props.context,
        cvId: window.cvCreatorConfig?.cvId || null,
        cvEntryId: props.cvEntryId,
        masterEntryId: props.masterEntryId
      })
      if (!result.success) return

      reviewItems.value = result.suggestions.map((s, i) => {
        const original = nonEmptyBullets.value[i] ?? s.original ?? ''
        const suggestion = (s.suggestion || '').trim()
        const unchanged = suggestion === '' || suggestion === original.trim()
        return { original, suggestion, unchanged, accepted: !unchanged }
      })
      showReview.value = true
    }

    const acceptedCount = computed(() => reviewItems.value.filter(item => item.accepted).length)
    const allAccepted = computed(() =>
      reviewItems.value.filter(item => !item.unchanged).every(item => item.accepted)
    )

    const toggleAll = () => {
      const next = !allAccepted.value
      reviewItems.value.forEach(item => {
        if (!item.unchanged) item.accepted = next
      })
    }

    const applyAccepted = () => {
      const newBullets = reviewItems.value.map(item => (item.accepted ? item.suggestion : item.original))
      emit('apply', newBullets)
      closeReview()
    }

    const closeReview = () => {
      showReview.value = false
      reviewItems.value = []
    }

    return {
      isOptimizing,
      optimizeError,
      showReview,
      reviewItems,
      hasBullets,
      acceptedCount,
      allAccepted,
      maxBulletChars: CV_CONFIG.validation.maxBulletChars,
      startOptimization,
      toggleAll,
      applyAccepted,
      closeReview
    }
  }
}
</script>

<style scoped>
/* Modal shell — mirrors CvCreationWizard's wizard-* classes */
.optimizer-backdrop {
  @apply fixed inset-0 z-50 flex items-center justify-center p-4;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(4px);
}
.optimizer-panel {
  @apply bg-white dark:bg-gray-950 border border-zinc-100 dark:border-zinc-800 rounded-2xl w-full max-w-2xl flex flex-col overflow-hidden;
  max-height: 90vh;
}
.optimizer-header {
  @apply flex items-start justify-between px-6 pt-6 pb-5 border-b border-zinc-100 dark:border-zinc-800 flex-shrink-0;
}
.optimizer-step-label {
  @apply text-xs font-semibold uppercase tracking-widest text-gold dark:text-gold-400 mb-1;
}
.optimizer-title {
  @apply text-lg font-semibold text-zinc-900 dark:text-white;
}
.optimizer-close {
  @apply p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors;
}
.optimizer-body {
  @apply px-6 py-5 flex-1 overflow-y-auto;
}
.optimizer-footer {
  @apply flex items-center justify-between px-6 py-4 border-t border-zinc-100 dark:border-zinc-800 flex-shrink-0;
}
.opt-btn-primary {
  @apply inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity;
}
.opt-btn-secondary {
  @apply inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium text-zinc-500 hover:text-zinc-900 dark:hover:text-white transition-colors;
}

/* Suggestion cards — mirror the wizard's entry-card selection pattern */
.suggestion-card {
  @apply relative border border-zinc-100 dark:border-zinc-800 rounded-xl p-3 cursor-pointer transition-colors hover:border-zinc-300 dark:hover:border-zinc-600;
}
.suggestion-card--selected {
  @apply border-gold dark:border-gold-500 bg-gold-50/40 dark:bg-gold-900/10;
}
.suggestion-card--static {
  @apply cursor-default hover:border-zinc-100 dark:hover:border-zinc-800;
}
.suggestion-card-check {
  @apply absolute top-2 right-2 w-4 h-4 rounded-full bg-gold flex items-center justify-center flex-shrink-0;
}

.modal-enter-active, .modal-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.modal-enter-from, .modal-leave-to {
  opacity: 0;
  transform: scale(0.97) translateY(4px);
}
</style>
